import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

//importing custom modules
import { SingleSelectInput } from "../../components/SelectInput";
import { Button } from "../../components";
import Input from "../../components/UserInput/Input";
import { Prefix } from "../../utils/constants";
import { newUserSchema } from "../../yup";

// importing styles
import style from "./nurse.module.css";
import { colors } from "../../theme/colors";

// importing api
import { useCreateUserMutation } from "../../services/api";
import { LabelBox } from "../../components";
import { RootState, useAppSelector } from "../../store";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof newUserSchema.__outputType;

/**
 * Renders the nurse creation form and submits a create-user payload.
 *
 * Returns:
 *   JSX.Element React component rendering the Add Nurse page.
 *
 * Notes:
 *   Expects `userRole` to be provided via route state and `health_org_id`
 *   to be available from authenticated user details.
 */
const AddNurse = () => {
  const navigation = useNavigate();
  const [createUser] = useCreateUserMutation();
  const { state } = useLocation();
  const userRole = state?.userRole;
  const selectedHealthOrgId =
    typeof state?.healthOrgId === "number" ? state.healthOrgId : undefined;

  const { userDetails } = useAppSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormState>({
    resolver: yupResolver(newUserSchema),
    mode: "onChange",
  });

  const onSubmit = async (values: FormState) => {
    if (!userRole) {
      setMessage({
        type: "error",
        text: "User role is missing. Please start from the Nurses page.",
      });
      return;
    }

    setLoading(true);
    try {
      const healthOrgId =
        userDetails?.health_org_id ?? selectedHealthOrgId ?? 0;
      let payload: CreateUserPayload = {
        given_name: values.given_name,
        surname: values.surname,
        mail: values.mail,
        health_org_id: healthOrgId,
        user_role_id: userRole,
      };

      if (values.prefix && values.prefix !== "") {
        payload.prefix = values.prefix;
      }

      if (values.phone && values.phone !== "") {
        payload.phone = values.phone;
      }

      await createUser(payload).unwrap();
      setMessage({
        type: "success",
        text: "Nurse added successfully.",
      });
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography className={style.detailHeading}>Add New Nurse</Typography>
        <Typography className={style.description}>
          Enter the below details to start adding a new nurse to Pegasus
          Cybersoft.
        </Typography>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <SingleSelectInput
              placeholder="Select Prefix"
              label="Prefix"
              register={register}
              name="prefix"
              errors={errors}
              data={Prefix}
            />
          </Box>

          <Box className={style.row}>
            <Input
              placeholder="First Name"
              label="First Name"
              required
              register={register}
              name="given_name"
              errors={errors}
            />
            <Input
              placeholder="Last Name"
              label="Last Name"
              required
              register={register}
              name="surname"
              errors={errors}
            />
          </Box>

          <Box className={style.row}>
            <Input
              placeholder="Email Address"
              label="Email Address"
              required
              register={register}
              name="mail"
              errors={errors}
            />
             <Input
              placeholder="Phone Number"
              label="Phone Number"
              register={register}
              name="phone"
              errors={errors}
            />
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label="Cancel"
            />
            <Button
              loading={loading}
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label="Save"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddNurse;
